import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Box,
  Button,
  Container,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Collapse,
  useScrollTrigger,
  useTheme,
  alpha,
  Stack,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  ExpandMore,
  ExpandLess,
  AccountCircle,
  ExitToApp,
} from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import { getNavigationStyles } from '@/styles/components/navigation';
import NavigationDropdown from './NavigationDropdown';
import { useAuth } from '@/contexts/AuthContext';
import { publicNavigation, authenticatedNavigation } from '@/navigation/config';
import DreamzConnectLogo from '@/assets/logos/dreamzconnect.svg';
import { getFeatureFlags } from '@/config/featureFlags';

interface NavigationProps {
  variant?: 'landing' | 'default';
  userRole?: string;
  hasDarkHero?: boolean;
}

export const Navigation: React.FC<NavigationProps> = ({ 
  userRole: propUserRole,
  hasDarkHero = false,
}) => {
  const theme = useTheme();
  const styles = getNavigationStyles(theme);
  const location = useLocation();
  const { userRole = propUserRole, isAuthenticated, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const featureFlags = getFeatureFlags();

  // Get appropriate navigation items based on auth state and feature flags
  const navigationItems = isAuthenticated && userRole && featureFlags.enableAuth
    ? authenticatedNavigation[userRole] || []
    : publicNavigation.filter(item => {
        if (item.path === '/login' || item.path === '/register') {
          return featureFlags.enableAuth && (item.path !== '/register' || featureFlags.enableRegistration);
        }
        return true;
      });

  // Detect scroll position for navbar styling
  const scrolled = useScrollTrigger({
    disableHysteresis: true,
    threshold: 50,
  });

  // Determine navbar style based on hasDarkHero prop
  const navbarStyle = {
    ...styles.navbar.transition,
    ...(hasDarkHero
      ? scrolled 
        ? styles.navbar.scrolled 
        : styles.navbar.transparent
      : styles.navbar.default
    ),
  };

  // Get text color based on scroll position and hero type
  const getTextColor = () => {
    if (hasDarkHero && !scrolled) return 'white';
    return theme.palette.text.primary;
  };

  // Handle mobile drawer
  const toggleDrawer = () => setMobileOpen(!mobileOpen);

  // Handle mobile menu item expansion
  const toggleExpanded = (path: string) => {
    setExpandedItems(prev =>
      prev.includes(path)
        ? prev.filter(item => item !== path)
        : [...prev, path]
    );
  };

  // Close mobile drawer on navigation
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Helper function to check if a path is active
  const isPathActive = (itemPath: string) => {
    // Exact match
    if (location.pathname === itemPath) return true;
    
    // Check if it's a sub-path but not the root dashboard
    if (itemPath !== `/${userRole}`) {
      return location.pathname.startsWith(itemPath + '/');
    }
    
    // For dashboard (root path), only match exact
    return false;
  };

  const handleNavigation = (_path: string) => {
    setMobileOpen(false); // Close mobile drawer
    window.scrollTo(0, 0); // Immediate scroll to top without smooth behavior
  };

  return (
    <>
      <AppBar
        position="fixed"
        sx={navbarStyle}
      >
        <Container maxWidth="xl">
          <Box sx={styles.navbar.container}>
            {/* Logo */}
            <Link 
              to="/" 
              style={{ textDecoration: 'none' }}
              onClick={() => handleNavigation('/')}
            >
              <Box sx={styles.logo.container}>
                <Box
                  component="img"
                  src={DreamzConnectLogo}
                  alt="DreamsConnect"
                  sx={{
                    ...styles.logo.image,
                    filter: hasDarkHero && !scrolled ? 'brightness(0) invert(1)' : 'none',
                  }}
                />
              </Box>
            </Link>

            {/* Desktop Navigation */}
            <Box sx={styles.menu.desktop}>
              {navigationItems.map((item) => (
                <NavigationDropdown
                  key={item.path}
                  item={item}
                  isActive={isPathActive(item.path)}
                  hasDarkHero={hasDarkHero}
                  scrolled={scrolled}
                  onNavigate={handleNavigation}
                />
              ))}

              {/* Auth Buttons */}
              {featureFlags.enableAuth && (
                isAuthenticated ? (
                  <>
                    <Button
                      startIcon={<AccountCircle sx={{ color: 'inherit' }} />}
                      component={Link}
                      to={`/${userRole}/profile`}
                      onClick={() => handleNavigation(`/${userRole}/profile`)}
                      data-active={location.pathname === `/${userRole}/profile`}
                      sx={{
                        ...styles.button.nav,
                        color: getTextColor(),
                        '& .MuiSvgIcon-root': {
                          color: 'inherit',
                        },
                      }}
                    >
                      Profile
                    </Button>
                    <Button
                      startIcon={<ExitToApp sx={{ color: 'inherit' }} />}
                      onClick={logout}
                      sx={{
                        ...styles.button.nav,
                        color: getTextColor(),
                        '& .MuiSvgIcon-root': {
                          color: 'inherit',
                        },
                      }}
                    >
                      Logout
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      component={Link}
                      to="/login"
                      onClick={() => handleNavigation('/login')}
                      sx={{
                        ...styles.button.nav,
                        color: getTextColor(),
                      }}
                    >
                      Log In
                    </Button>
                    {featureFlags.enableRegistration && (
                      <Button
                        component={Link}
                        to="/register"
                        onClick={() => handleNavigation('/register')}
                        variant="contained"
                        sx={{
                          ...styles.button.auth.register,
                          ...(hasDarkHero && !scrolled && {
                            background: 'white',
                            color: 'primary.main',
                            '&:hover': {
                              background: alpha('#fff', 0.9),
                            },
                          }),
                        }}
                      >
                        Sign Up
                      </Button>
                    )}
                  </>
                )
              )}
            </Box>

            {/* Mobile Menu Toggle */}
            <Box sx={styles.menu.mobile}>
              <IconButton
                onClick={toggleDrawer}
                sx={{ 
                  color: getTextColor(),
                }}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          </Box>
        </Container>
      </AppBar>
      
      {/* Mobile Navigation Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={toggleDrawer}
        sx={{
          ...styles.mobile.drawer,
          '& .MuiDrawer-paper': {
            width: '70vw',
            maxWidth: '280px',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
          }
        }}
      >
        {/* Header */}
        <Box sx={{
          ...styles.mobile.header,
          py: 0.75,
          px: 1.25,
          minHeight: '2.75rem',
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <Box sx={{ 
            ...styles.logo.text, 
            fontSize: '1rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            Menu
          </Box>
          <IconButton 
            onClick={toggleDrawer}
            sx={{ 
              p: 0.25,
              ml: 1,
              flexShrink: 0,
            }}
          >
            <CloseIcon sx={{ fontSize: '1.25rem' }} />
          </IconButton>
        </Box>
        
        {/* Content Container */}
        <Box sx={{ 
          flex: 1,
          overflowY: 'auto',
          pl: 0, // Remove left padding
          pr: 1.25, // Keep right padding
        }}>
          {/* Main List Container */}
          <List disablePadding>
            {navigationItems.map((item) => {
              const isExpanded = expandedItems.includes(item.path);
              const hasChildren = item.children && item.children.length > 0;
              const isActive = isPathActive(item.path);
              
              return (
                <React.Fragment key={item.path}>
                  <ListItem
                    component={item.isExternal ? 'a' : hasChildren ? 'div' : Link}
                    href={item.isExternal ? item.path : undefined}
                    to={!item.isExternal && !hasChildren ? item.path : undefined}
                    onClick={(_e: React.MouseEvent) => {
                      if (hasChildren) {
                        toggleExpanded(item.path);
                      } else if (!item.isExternal) {
                        handleNavigation(item.path);
                      }
                    }}
                    target={item.isExternal ? '_blank' : undefined}
                    rel={item.isExternal ? 'noopener noreferrer' : undefined}
                    disablePadding
                    sx={{
                      ...styles.mobile.item,
                      ...(isActive && styles.mobile.activeItem),
                      display: 'flex',
                      alignItems: 'center',
                      minHeight: '2.75rem',
                      py: 0.5,
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    <Box sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      flex: 1,
                      minWidth: 0, // Enable text truncation
                    }}>
                      {item.icon && (
                        <Box
                          component={item.icon}
                          sx={{ 
                            fontSize: '1.25rem',
                            opacity: 0.7,
                            flexShrink: 0,
                          }}
                        />
                      )}
                      <ListItemText 
                        primary={item.title}
                        sx={{
                          m: 0,
                          flex: '1 1 auto',
                          minWidth: 0,
                          '& .MuiListItemText-primary': {
                            fontSize: '0.9rem',
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }
                        }}
                      />
                      {hasChildren && (
                        <Box sx={{ flexShrink: 0 }}>
                          {isExpanded ? (
                            <ExpandLess sx={{ fontSize: '1.25rem' }} />
                          ) : (
                            <ExpandMore sx={{ fontSize: '1.25rem' }} />
                          )}
                        </Box>
                      )}
                    </Box>
                  </ListItem>
                  {hasChildren && item.children && (
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <List disablePadding sx={{ pl: 2.5 }}>
                        {item.children.map((child) => (
                          <ListItem
                            key={child.path}
                            component={child.isExternal ? 'a' : Link}
                            href={child.isExternal ? child.path : undefined}
                            to={!child.isExternal ? child.path : undefined}
                            onClick={() => !child.isExternal && handleNavigation(child.path)}
                            target={child.isExternal ? '_blank' : undefined}
                            rel={child.isExternal ? 'noopener noreferrer' : undefined}
                            disablePadding
                            sx={{
                              ...styles.mobile.item,
                              display: 'flex',
                              alignItems: 'center',
                              minHeight: '2.5rem',
                              py: 0.5,
                              '&:hover': {
                                bgcolor: 'action.hover',
                              },
                            }}
                          >
                            <Box sx={{ 
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                              flex: 1,
                              minWidth: 0,
                            }}>
                              {child.icon && (
                                <Box
                                  component={child.icon}
                                  sx={{ 
                                    fontSize: '1rem',
                                    opacity: 0.7,
                                    flexShrink: 0,
                                  }}
                                />
                              )}
                              <ListItemText 
                                primary={child.title}
                                sx={{
                                  m: 0,
                                  flex: '1 1 auto',
                                  minWidth: 0,
                                  '& .MuiListItemText-primary': {
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }
                                }}
                              />
                            </Box>
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  )}
                </React.Fragment>
              );
            })}
          </List>
        </Box>

        {/* Auth Section */}
        <Box sx={{ 
          borderTop: 1,
          borderColor: 'divider',
          p: 1.25,
        }}>
          {isAuthenticated ? (
            <List disablePadding>
              <ListItem
                component={Link}
                to={`/${userRole}/profile`}
                onClick={() => handleNavigation(`/${userRole}/profile`)}
                disablePadding
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  minHeight: '2.5rem',
                  py: 0.5,
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <Box sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  flex: 1,
                  minWidth: 0,
                }}>
                  <AccountCircle sx={{ 
                    fontSize: '1.25rem',
                    opacity: 0.7,
                    flexShrink: 0,
                  }} />
                  <ListItemText 
                    primary="Profile"
                    sx={{
                      m: 0,
                      flex: '1 1 auto',
                      minWidth: 0,
                      '& .MuiListItemText-primary': {
                        fontSize: '0.9rem',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }
                    }}
                  />
                </Box>
              </ListItem>
              <ListItem
                component="button"
                onClick={logout}
                disablePadding
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  minHeight: '2.5rem',
                  py: 0.5,
                  width: '100%',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <Box sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  flex: 1,
                  minWidth: 0,
                }}>
                  <ExitToApp sx={{ 
                    fontSize: '1.25rem',
                    opacity: 0.7,
                    flexShrink: 0,
                  }} />
                  <ListItemText 
                    primary="Logout"
                    sx={{
                      m: 0,
                      flex: '1 1 auto',
                      minWidth: 0,
                      '& .MuiListItemText-primary': {
                        fontSize: '0.9rem',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }
                    }}
                  />
                </Box>
              </ListItem>
            </List>
          ) : (
            <Stack spacing={1}>
              {featureFlags.enableAuth && (
                <Button
                  component={Link}
                  to="/login"
                  onClick={() => handleNavigation('/login')}
                  fullWidth
                  sx={{ 
                    ...styles.button.nav, 
                    fontSize: '0.9rem',
                  }}
                >
                  Log In
                </Button>
              )}
              {featureFlags.enableAuth && featureFlags.enableRegistration && (
                <Button
                  component={Link}
                  to="/register"
                  onClick={() => handleNavigation('/register')}
                  variant="contained"
                  fullWidth
                  sx={{ 
                    ...styles.button.auth.register,
                    fontSize: '0.9rem',
                  }}
                >
                  Sign Up
                </Button>
              )}
            </Stack>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default Navigation;